import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Switch,
  TimePicker,
  Tooltip,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { useGetCategories, useAddExam, useEditExam, useGetUser, useGetExamGroups, useAxios } from "@hooks";
import { GradingConfigurationSelect } from "../../grading/GradingConfigurationSelect";
import { Exam, ExamQuestionType } from "@types";
import { SetStateAction, useEffect } from "react";
import { useQueries } from "@tanstack/react-query";
import dayjs from 'dayjs';
import durationPlugin from 'dayjs/plugin/duration';
import { setFieldValues } from "./helpers";
import { QuestionCircleOutlined } from "@ant-design/icons";

dayjs.extend(durationPlugin);

interface Props {
  setActiveKey: React.Dispatch<React.SetStateAction<string>>;
  setCreatedExam: React.Dispatch<SetStateAction<Exam | undefined>>;
  exam?: Exam;
  id?: string;
}

export const ExamPrimarySettings = ({ setActiveKey, setCreatedExam, id, exam }: Props) => {
  const [primaryForm] = Form.useForm();
  const { t } = useTranslation();
  const { data: categoriesData, isLoading: categoriesLoading } =
    useGetCategories();
  const { data: currentUser } = useGetUser();
  const { data: examGroups } = useGetExamGroups(id);
  const axios = useAxios();

  const groupQuestionResults = useQueries({
    queries: (examGroups ?? []).map(group => ({
      queryKey: ["examGroupQuestions", group.id],
      queryFn: async () => {
        const response = await axios.get(`/api/examgroup/${group.id}/questions`);
        return response.data;
      },
      staleTime: Infinity,
      enabled: !!group.id,
    })),
  });

  const hasEssayOrShortAnswer = groupQuestionResults.some(result =>
    result.data?.items?.some((q: any) =>
      q.questionType?.id === ExamQuestionType.EssayQuestion ||
      q.questionType?.id === ExamQuestionType.ShortAnswer ||
      q.type === ExamQuestionType.EssayQuestion ||
      q.type === ExamQuestionType.ShortAnswer
    )
  );

  // Watch grading configuration to conditionally show/hide percentage field
  const gradingConfigId = Form.useWatch('gradingConfigurationId', primaryForm);
  
  // Watch hide statistics toggle to conditionally show/hide completion message editor
  const hideStatistics = Form.useWatch('hideStatisticsAfterCompletion', primaryForm);

  const addExam = useAddExam();
  const editExam = useEditExam();

  const handleSubmit = (value: any) => {
    if (exam) {
      editExam.mutate({ ...exam, ...value }, {
        onSuccess(_, variables) {
          setCreatedExam(variables);
          setActiveKey("2");
        },
      });
    } else {
      addExam.mutate(value, {
        onSuccess(data) {
          setCreatedExam(data);
          setActiveKey("2");
        },
      });
    }
   
  };

  useEffect(() => {
    if (id) {
     setFieldValues(exam, primaryForm);
    }
  }, [exam, id, primaryForm]);

  // Clear percentage when grading config is selected (it defines its own pass criteria)
  useEffect(() => {
    if (gradingConfigId) {
      primaryForm.setFieldValue('percentageToPass', undefined);
    }
  }, [gradingConfigId, primaryForm]);

  // Set default completion message when hide statistics is first enabled
  useEffect(() => {
    if (hideStatistics) {
      const currentMessage = primaryForm.getFieldValue('completionMessage');
      if (!currentMessage || currentMessage.trim() === '' || currentMessage === '<p><br></p>') {
        primaryForm.setFieldValue('completionMessage', t("pages.examsManagment.primarySettingsTab.defaultCompletionMessage"));
      }
    }
  }, [hideStatistics, primaryForm, t]);


  return (
    <Form layout="vertical" form={primaryForm} onFinish={handleSubmit}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={t("pages.examsManagment.primarySettingsTab.examName")}
            name={["title"]}
            rules={[
              { required: true, message: t("pages.examsManagment.primarySettingsTab.examNameDesc") },
            ]}
          >
            <Input placeholder={t("pages.examsManagment.primarySettingsTab.examNamePlaceholder")} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={t("pages.examsManagment.primarySettingsTab.categories")}
            name={["categories"]}
            rules={[
              { required: true, message: t("pages.examsManagment.primarySettingsTab.categoriesDesc") },
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              filterOption={(input, option: any) =>
                option?.label!.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              loading={categoriesLoading}
              maxTagCount={'responsive'}
              options={categoriesData?.map((category: any) => ({
                value: category.id,
                label: category.name,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        name={["description"]}
        label={t("pages.examsManagment.primarySettingsTab.examDesc")}
      >
        <ReactQuill id="exam-text" theme="snow" />
      </Form.Item>
      <Space wrap>
        <Form.Item
          label={t("pages.examsManagment.primarySettingsTab.examDuration")}
          name={["timeInSeconds"]}
          rules={[{ required: true, message: t("pages.examsManagment.primarySettingsTab.examDurationDesc") }]}
        >
          <TimePicker
            needConfirm={false}
            style={{ width: "100%" }}
            showNow={false}
            format={"H[h] mm[min]"}
            minuteStep={5}
          />
        </Form.Item>
        {!gradingConfigId && (
          <Form.Item
            label={t("pages.examsManagment.primarySettingsTab.percentage")}
            name={["percentageToPass"]}
            rules={[{ required: true, message: t("pages.examsManagment.primarySettingsTab.percentageDesc") }]}
          >
            <InputNumber suffix="%" />
          </Form.Item>
        )}
        {currentUser?.showP365Feature && !hasEssayOrShortAnswer && (
          <Form.Item
          label={<Space>
            {t("pages.examsManagment.primarySettingsTab.p365Exam")} 
              <Tooltip title={t("pages.examsManagment.primarySettingsTab.p365ExamDescription")}>
                <QuestionCircleOutlined />
              </Tooltip>
          </Space>}
          name={["isP365Exam"]}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        )}
        <Form.Item
          label={<Space>
            {t("pages.examsManagment.primarySettingsTab.gradingConfig", "Grading Configuration")}
              <Tooltip title={t("pages.examsManagment.primarySettingsTab.gradingConfigDescription", "Select a grading configuration to apply specific scoring rules to this exam")}>
                <QuestionCircleOutlined />
              </Tooltip>
          </Space>}
          name={["gradingConfigurationId"]}
        >
          <GradingConfigurationSelect />
        </Form.Item>
      </Space>
      <Form.Item
        name={["applicantNote"]}
        label={t("pages.examsManagment.primarySettingsTab.note")}
      >
        <TextArea rows={4} />
      </Form.Item>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name={["allowReturn"]}
            initialValue={true}
            valuePropName="checked"
            label={t("pages.examsManagment.primarySettingsTab.prevQuestion")}
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name={["hideStatisticsAfterCompletion"]}
            initialValue={false}
            valuePropName="checked"
            label={<Space>
              {t("pages.examsManagment.primarySettingsTab.hideStatistics")}
              <Tooltip title={t("pages.examsManagment.primarySettingsTab.hideStatisticsDescription")}>
                <QuestionCircleOutlined />
              </Tooltip>
            </Space>}
          >
            <Switch />
          </Form.Item>
        </Col>
        {/* <Col span={8}>
          <Form.Item
            valuePropName="checked"
            name={["arrangeQuestionsIntoGroups"]}
            label={t("pages.examsManagment.primarySettingsTab.orderQuestions")}
          >
            <Switch />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            valuePropName="checked"
            name={["areGroupsShuffled"]}
            label={t("pages.examsManagment.primarySettingsTab.shuffle")}
          >
            <Switch />
          </Form.Item>
        </Col> */}
      </Row>
      {hideStatistics && (
        <Form.Item
          name={["completionMessage"]}
          label={t("pages.examsManagment.primarySettingsTab.completionMessage")}
          rules={[
            {
              validator: (_, value) => {
                if (value && value.length > 5000) {
                  return Promise.reject(new Error('Completion message must be less than 5000 characters'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <ReactQuill id="completion-message-text" theme="snow" />
        </Form.Item>
      )}
      <Form.Item
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 0,
        }}
      >
        <Space>
          <Button>{t("pages.examsManagment.cancel")}</Button>
          <Button type="dashed" htmlType="submit" loading={exam ? editExam.isPending : addExam.isPending}>{t("pages.examsManagment.saveQuit")}</Button>
          <Button type="primary" htmlType="submit" loading={exam ? editExam.isPending : addExam.isPending}>
            {t("pages.examsManagment.next")}
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
